import React from 'react';
import { motion } from 'framer-motion';

interface NavItem {
    id: string;
    label: string;
    icon: React.ReactNode;
    badge?: number;
    badgeVariant?: 'danger' | 'warning';
}

interface DashboardSidebarProps {
    activePage: string;
    onNavigate: (page: string) => void;
    mobileOpen: boolean;
    onCloseMobile: () => void;
}

const navSections: { label: string; items: NavItem[] }[] = [
    {
        label: 'Главное',
        items: [
            {
                id: 'dashboard',
                label: 'Обзор',
                icon: (
                    <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <rect x="3" y="3" width="7" height="7" rx="1"/>
                        <rect x="14" y="3" width="7" height="7" rx="1"/>
                        <rect x="14" y="14" width="7" height="7" rx="1"/>
                        <rect x="3" y="14" width="7" height="7" rx="1"/>
                    </svg>
                )
            },
            {
                id: 'balances',
                label: 'Остатки ДС',
                icon: (
                    <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path d="M20 12V8H6a2 2 0 0 1-2-2c0-1.1.9-2 2-2h12v4"/>
                        <path d="M20 12v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-8h16z"/>
                    </svg>
                )
            },
            {
                id: 'analytics',
                label: 'Статистика',
                icon: (
                    <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
                    </svg>
                )
            }
        ]
    },
    {
        label: 'События',
        items: [
            {
                id: 'incidents',
                label: 'Сбои',
                icon: (
                    <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                        <line x1="12" y1="9" x2="12" y2="13"/>
                        <line x1="12" y1="17" x2="12.01" y2="17"/>
                    </svg>
                )
            },
            {
                id: 'messages',
                label: 'Уведомления',
                icon: (
                    <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                    </svg>
                )
            }
        ]
    },
    {
        label: 'Система',
        items: [
            {
                id: 'settings',
                label: 'Настройки',
                icon: (
                    <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <circle cx="12" cy="12" r="3"/>
                        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
                    </svg>
                )
            },
            {
                id: 'about',
                label: 'О продукте',
                icon: (
                    <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <circle cx="12" cy="12" r="10"/>
                        <line x1="12" y1="16" x2="12" y2="12"/>
                        <line x1="12" y1="8" x2="12.01" y2="8"/>
                    </svg>
                )
            }
        ]
    }
];

export const DashboardSidebar: React.FC<DashboardSidebarProps> = ({
    activePage,
    onNavigate,
    mobileOpen,
    onCloseMobile
}) => {
    const handleNavigate = (page: string) => {
        onNavigate(page);
        onCloseMobile();
    };

    return (
        <>
            {mobileOpen && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 top-[52px] bg-black/40 backdrop-blur-sm z-[15] md:hidden"
                    onClick={onCloseMobile}
                />
            )}

            <nav className={`
                flex-col w-[200px] shrink-0 bg-[var(--bg-elevated)] border-r border-[var(--border)] py-2.5 px-2 overflow-y-auto z-20
                ${mobileOpen ? 'fixed top-[52px] left-0 bottom-0 w-[240px] shadow-xl flex' : 'hidden md:flex'}
            `}>
                {navSections.map((section, idx) => (
                    <div key={section.label} className={idx > 0 ? 'mt-2' : ''}>
                        <div className="text-[10px] font-medium text-[var(--text-muted)] uppercase tracking-[0.06em] px-2.5 py-2">
                            {section.label}
                        </div>
                        {section.items.map(item => {
                            const isActive = activePage === item.id;
                            return (
                                <motion.a
                                    key={item.id}
                                    onClick={() => handleNavigate(item.id)}
                                    className={`
                                        flex items-center gap-2 px-2.5 py-[7px] rounded-lg text-[13px] cursor-pointer transition-all relative
                                        ${isActive 
                                        ? 'bg-[var(--brand-dim)] text-[var(--brand)] font-medium' 
                                        : 'text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] hover:text-[var(--text)]'
                                        }
                                    `}
                                    whileHover={{ x: isActive ? 0 : 2 }}
                                    whileTap={{ scale: 0.98 }}
                                >
                                    {isActive && (
                                        <motion.div
                                            layoutId="activeIndicator"
                                            className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-4 bg-[var(--brand)] rounded-r"
                                        />
                                    )}
                                    {item.icon}
                                    <span>{item.label}</span>
                                    {item.badge && (
                                        <span className={`
                                            ml-auto min-w-[18px] h-[18px] px-1.5 rounded-full text-[10px] font-semibold flex items-center justify-center
                                            ${item.badgeVariant === 'danger' 
                                                ? 'bg-[var(--status-down-bg)] text-[var(--status-down-text)]' 
                                                : 'bg-[var(--status-warn-bg)] text-[var(--status-warn-text)]'
                                            }
                                        `}>
                                            {item.badge}
                                        </span>
                                    )}
                                </motion.a>
                            );
                        })}
                    </div>
                ))}

                <div className="mt-auto pt-4 px-2.5 flex items-center gap-2 text-[11px] text-[var(--text-muted)]">
                    <div className="w-1.5 h-1.5 rounded-full bg-[var(--status-up)] animate-pulse" />
                    <span>Данные актуальны</span>
                </div>
            </nav>
        </>
    );
};